import { Link } from 'react-router-dom';
import { Check, GraduationCap, Briefcase, Building, ArrowRight, Star, Sparkles } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';

const PricingPage = () => {
  const mainPackages = [
    {
      name: 'Consultation Simple',
      description: 'Idéal pour une première prise de contact et des conseils ponctuels',
      price: '15 000',
      priceStudent: '10 000',
      unit: 'par séance',
      features: [
        'Consultation d\'1 heure',
        'Analyse de votre situation',
        'Conseils personnalisés',
        'Orientation vers les solutions adaptées',
        'Compte-rendu écrit'
      ],
      popular: false,
      icon: Sparkles
    },
    {
      name: 'Pack Création Entreprise',
      description: 'Accompagnement complet pour créer votre entreprise au Sénégal',
      price: '150 000',
      priceStudent: '95 000',
      unit: 'forfait complet',
      features: [
        'Étude de faisabilité',
        'Choix de la forme juridique',
        'Rédaction des statuts',
        'Immatriculation RCCM & NINEA',
        'Ouverture compte bancaire',
        'Accompagnement 3 mois post-création',
        'Support juridique inclus'
      ],
      popular: true,
      icon: Building
    },
    {
      name: 'Pack Entrepreneur Pro',
      description: 'Pour les entrepreneurs qui veulent aller plus loin',
      price: '350 000',
      priceStudent: '250 000',
      unit: 'forfait complet',
      features: [
        'Tout le Pack Création Entreprise',
        'Business plan professionnel',
        'Stratégie de financement',
        'Accompagnement levée de fonds',
        'Formation gestion d\'entreprise',
        'Accompagnement 6 mois',
        'Accès réseau partenaires'
      ],
      popular: false,
      icon: Briefcase
    }
  ];

  const additionalServices = [
    { service: 'Rédaction de contrats', price: '25 000', priceStudent: '15 000', unit: 'par contrat' },
    { service: 'Conseil juridique approfondi', price: '35 000', priceStudent: '20 000', unit: 'par séance (2h)' },
    { service: 'Audit juridique entreprise', price: '100 000', priceStudent: '70 000', unit: 'forfait' },
    { service: 'Formation entrepreneuriat (10h)', price: '75 000', priceStudent: '45 000', unit: 'par personne' },
    { service: 'Accompagnement financement', price: '50 000', priceStudent: '30 000', unit: '+ 5% si succès' },
    { service: 'Formalisation administrative', price: '40 000', priceStudent: '25 000', unit: 'forfait' },
  ];

  const studyAbroadPackages = [
    {
      destination: 'France',
      flag: '🇫🇷',
      price: '250 000',
      priceStudent: '175 000',
      features: [
        'Orientation et choix d\'établissement',
        'Constitution du dossier Campus France',
        'Préparation aux entretiens',
        'Accompagnement visa étudiant',
        'Recherche de logement',
        'Conseils intégration'
      ]
    },
    {
      destination: 'Canada',
      flag: '🇨🇦',
      price: '300 000',
      priceStudent: '200 000',
      features: [
        'Orientation et choix d\'université',
        'Dossier d\'admission complet',
        'Demande de permis d\'études',
        'Préparation à l\'entretien biométrique',
        'Conseils CAQ (Québec)',
        'Guide installation Canada'
      ]
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 md:py-32 gradient-hero" data-testid="pricing-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Nos Tarifs</span>
          <h1 className="hero-title mt-4" data-testid="pricing-title">
            Des tarifs <span className="text-[#006F33]">transparents</span> et accessibles
          </h1>
          <p className="hero-subtitle mt-6 max-w-2xl mx-auto">
            Investissez dans votre avenir avec des solutions adaptées à votre budget. 
            Tarifs spéciaux pour les étudiants et jeunes entrepreneurs.
          </p>
          
          {/* Student Badge */}
          <div className="mt-8 inline-flex items-center gap-2 bg-[#E6C200]/20 text-[#B39500] px-6 py-3 rounded-full">
            <GraduationCap className="w-5 h-5" />
            <span className="font-semibold">Réduction jusqu'à -35% pour les étudiants</span>
          </div>
        </div>
      </section>

      {/* Main Packages */}
      <section className="py-20 md:py-32 bg-white" data-testid="main-packages">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Forfaits Principaux</span>
            <h2 className="section-title mt-4">Choisissez votre accompagnement</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mainPackages.map((pkg, index) => (
              <div 
                key={index}
                className={`relative rounded-2xl p-8 ${
                  pkg.popular 
                    ? 'bg-[#006F33] text-white ring-4 ring-[#006F33]/20' 
                    : 'bg-white border border-slate-200'
                }`}
                data-testid={`package-${index}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#E6C200] text-[#1A1A1A] px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                      <Star className="w-4 h-4" /> Plus populaire
                    </span>
                  </div>
                )}
                
                <div className={`w-14 h-14 rounded-xl ${pkg.popular ? 'bg-white/20' : 'bg-[#006F33]/10'} flex items-center justify-center mb-6`}>
                  <pkg.icon className={`w-7 h-7 ${pkg.popular ? 'text-white' : 'text-[#006F33]'}`} />
                </div>
                
                <h3 className={`text-xl font-bold mb-2 ${pkg.popular ? 'text-white' : 'text-[#1A1A1A]'}`}>
                  {pkg.name}
                </h3>
                <p className={`text-sm mb-6 ${pkg.popular ? 'text-white/80' : 'text-slate-600'}`}>
                  {pkg.description}
                </p>
                
                {/* Prix standard */}
                <div className="mb-2">
                  <span className={`text-3xl font-bold ${pkg.popular ? 'text-white' : 'text-[#1A1A1A]'}`}>
                    {pkg.price}
                  </span>
                  <span className={`text-sm ml-1 ${pkg.popular ? 'text-white/70' : 'text-slate-500'}`}>
                    FCFA
                  </span>
                  <span className={`text-sm ml-2 ${pkg.popular ? 'text-white/60' : 'text-slate-400'}`}>
                    {pkg.unit}
                  </span>
                </div>
                
                {/* Prix étudiant */}
                <div className={`mb-6 flex items-center gap-2 ${pkg.popular ? 'text-[#E6C200]' : 'text-[#006F33]'}`}>
                  <GraduationCap className="w-4 h-4" />
                  <span className="text-sm font-medium">Étudiant : {pkg.priceStudent} FCFA</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${pkg.popular ? 'text-[#E6C200]' : 'text-[#006F33]'}`} />
                      <span className={`text-sm ${pkg.popular ? 'text-white/90' : 'text-slate-600'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                
                <Link to="/contact">
                  <Button 
                    className={`w-full ${
                      pkg.popular 
                        ? 'bg-white text-[#006F33] hover:bg-slate-100' 
                        : 'btn-primary'
                    }`}
                    data-testid={`package-cta-${index}`}
                  >
                    Choisir ce forfait
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Abroad Section */}
      <section className="py-20 md:py-32 bg-[#F9F9F7]" data-testid="study-abroad">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Nouveau Service</span>
            <h2 className="section-title mt-4">Études à l'Étranger</h2>
            <p className="section-subtitle mt-4 max-w-2xl mx-auto">
              Réalisez votre rêve d'étudier en France ou au Canada. Nous vous accompagnons de A à Z.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {studyAbroadPackages.map((pkg, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100" data-testid={`study-${pkg.destination.toLowerCase()}`}>
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-5xl">{pkg.flag}</span>
                  <div>
                    <h3 className="text-2xl font-bold text-[#1A1A1A]">Étudier en {pkg.destination}</h3>
                    <p className="text-slate-500">Accompagnement complet</p>
                  </div>
                </div>
                
                {/* Prix */}
                <div className="bg-[#006F33]/5 rounded-xl p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-600">Tarif standard</span>
                    <span className="text-xl font-bold text-[#1A1A1A]">{pkg.price} FCFA</span>
                  </div>
                  <div className="flex justify-between items-center text-[#006F33]">
                    <span className="flex items-center gap-1">
                      <GraduationCap className="w-4 h-4" /> Tarif étudiant
                    </span>
                    <span className="font-bold">{pkg.priceStudent} FCFA</span>
                  </div>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#006F33] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link to="/contact">
                  <Button className="btn-outline w-full">
                    En savoir plus
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services Table */}
      <section className="py-20 md:py-32 bg-white" data-testid="additional-pricing">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Services à la Carte</span>
            <h2 className="section-title mt-4">Tarifs détaillés</h2>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Service</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-slate-600">Tarif Standard</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-[#006F33]">
                      <span className="flex items-center justify-center gap-1">
                        <GraduationCap className="w-4 h-4" /> Tarif Étudiant
                      </span>
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-slate-600">Unité</th>
                  </tr>
                </thead>
                <tbody>
                  {additionalServices.map((item, index) => (
                    <tr key={index} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-[#1A1A1A]">{item.service}</td>
                      <td className="px-6 py-4 text-center text-slate-700">{item.price} FCFA</td>
                      <td className="px-6 py-4 text-center text-[#006F33] font-semibold">{item.priceStudent} FCFA</td>
                      <td className="px-6 py-4 text-center text-slate-500 text-sm">{item.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <p className="text-center text-slate-500 mt-6 text-sm">
            * Les tarifs étudiants sont réservés aux étudiants sur présentation d'une carte étudiant valide.
          </p>
        </div>
      </section>

      {/* FAQ / Notes */}
      <section className="py-20 md:py-32 gradient-brand" data-testid="pricing-notes">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6">Conditions de paiement</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#006F33] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Paiement en 2-3 fois possible pour les forfaits</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#006F33] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Paiement par Wave, Orange Money, virement bancaire</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#006F33] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Facture officielle fournie</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#006F33] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Première consultation gratuite de 30 min</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6">Garanties</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#006F33] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Satisfaction garantie ou remboursement sous 7 jours</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#006F33] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Accompagnement personnalisé avec un expert dédié</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#006F33] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Confidentialité totale de vos informations</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#006F33] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Support disponible 6j/7</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#006F33]" data-testid="pricing-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Prêt à investir dans votre avenir ?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Contactez-nous pour une consultation gratuite et recevez un devis personnalisé.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#006F33] hover:bg-slate-100 shadow-lg px-8 py-3 rounded-full font-medium flex items-center gap-2 mx-auto" data-testid="pricing-cta-button">
              Demander un devis gratuit
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default PricingPage;
